import { FC, FormEvent, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';

import type { UserInfoRes } from '@/common/interface';
import { Icon } from '@/components';
import {
  applyEnterpriseAssociation,
  isEnterpriseMember,
  withdrawEnterpriseAssociation,
} from '@/services/enterpriseAssociation';
import { useEnterpriseAssociation } from '@/hooks/useEnterpriseAssociation';

type Props = { userInfo: UserInfoRes };

const statusText: Record<string, string> = {
  none: '未申请',
  pending: '审核中',
  approved: '已绑定',
  rejected: '已拒绝',
  expired: '已过期',
  withdrawn: '已撤回',
  blocked: '已暂停',
};

const formatDate = (value?: string) => (value ? new Date(value).toLocaleString('zh-CN') : '—');

const EnterpriseAssociationEntry: FC<Props> = ({ userInfo }) => {
  const { association, refresh } = useEnterpriseAssociation(Boolean(userInfo?.id));
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [enterpriseCode, setEnterpriseCode] = useState('');
  const [employeeName, setEmployeeName] = useState(userInfo?.display_name || '');
  const [description, setDescription] = useState('');
  const bound = isEnterpriseMember(association);

  const open = () => {
    setEmployeeName(userInfo?.display_name || '');
    setVisible(true);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const requestID = crypto.randomUUID();
      await applyEnterpriseAssociation(
        {
          enterprise_code: enterpriseCode.trim().toLowerCase(),
          employee_name: employeeName.trim(),
          description: description.trim(),
          client_request_id: requestID,
          client_version: 'answer-web-v1',
        },
        requestID,
      );
      await refresh();
      setEnterpriseCode('');
      setDescription('');
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    if (!association?.application_id || !association.application_version) return;
    setBusy(true);
    try {
      const requestID = crypto.randomUUID();
      await withdrawEnterpriseAssociation(
        association.application_id,
        {
          client_request_id: requestID,
          expected_application_version: association.application_version,
          client_version: 'answer-web-v1',
        },
        requestID,
      );
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const state = association?.state || 'none';
  return (
    <>
      <Button variant={bound ? 'outline-success' : 'outline-primary'} size="sm" onClick={open} className="me-2">
        <Icon name="building" className="me-1" />
        {bound ? '企业服务' : '绑定企业'}
      </Button>
      <Modal show={visible} onHide={() => setVisible(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{bound ? '企业服务' : '绑定企业'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="small text-muted mb-3">
            当前状态：<strong>{statusText[state] || state}</strong>
          </div>
          {association?.enterprise && (
            <div className="alert alert-success py-2">
              企业：{association.enterprise.display_name || association.enterprise.display_name_masked || '已关联企业'}
              <br />加入时间：{formatDate(association.joined_at)}
            </div>
          )}
          {state === 'pending' && association && (
            <>
              <p className="small text-muted">申请时间：{formatDate(association.applied_at)}。管理员处理前可撤回申请。</p>
              <Button variant="outline-danger" disabled={busy || !association.can_withdraw} onClick={withdraw}>
                撤回申请
              </Button>
            </>
          )}
          {!bound && state !== 'pending' && (association?.can_start_new_application !== false) && (
            <Form onSubmit={submit}>
              <Form.Group className="mb-3">
                <Form.Label>企业识别码</Form.Label>
                <Form.Control value={enterpriseCode} onChange={(e) => setEnterpriseCode(e.target.value)} minLength={64} maxLength={64} required placeholder="64 位企业识别码" />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>姓名</Form.Label>
                <Form.Control value={employeeName} onChange={(e) => setEmployeeName(e.target.value)} maxLength={100} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>备注（可选）</Form.Label>
                <Form.Control as="textarea" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} />
              </Form.Group>
              <Button type="submit" disabled={busy}>{busy ? '提交中…' : '提交绑定申请'}</Button>
            </Form>
          )}
          {state !== 'none' && state !== 'pending' && !bound && (
            <div className="small text-muted mt-3">申请记录：{statusText[state] || state}，可重新提交新的企业申请。</div>
          )}
          {bound && association && <div className="small text-muted mt-3">进入左侧“企业服务”可查看部门、申请额度及历史审批记录。</div>}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default EnterpriseAssociationEntry;
